import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { componentScope } from "../../lib/cssScope";
import { Button } from "../ui/Button";

export type ComplianceFormQuestion = {
  id: string;
  code?: string;
  text_en?: string | null;
  text_es?: string | null;
  text_ru?: string | null;
  weight?: number | null;
  type?: string | null;
  scaleMin?: number | null;
  scaleMax?: number | null;
  metadata?: Record<string, unknown> | null;
};

export type ComplianceFormCategory = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  standardCode?: string;
  taxonomy?: unknown;
  questions: ComplianceFormQuestion[];
};

interface ComplianceFormCardProps {
  category: ComplianceFormCategory;
  isExpanded: boolean;
  answers: Record<string, number>;
  onToggle: (categoryId: string) => void;
  onAnswerChange: (categoryId: string, questionId: string, value: number) => void;
  getQuestionLabel: (
    category: ComplianceFormCategory,
    question: ComplianceFormQuestion,
  ) => string;
  statusMessage?: string;
  statusVariant?: "default" | "saving" | "pending" | "saved" | "offline";
  unsavedAlert?: string | null;
  disabled?: boolean;
}

const buildScaleValues = (question: ComplianceFormQuestion): number[] => {
  const minRaw = Number.isFinite(question.scaleMin)
    ? Number(question.scaleMin)
    : 1;
  const maxRaw = Number.isFinite(question.scaleMax)
    ? Number(question.scaleMax)
    : 5;
  const min = Math.round(Number.isNaN(minRaw) ? 1 : minRaw);
  const max = Math.round(Number.isNaN(maxRaw) ? 5 : maxRaw);
  if (min >= max) return [min];
  const values: number[] = [];
  for (let value = min; value <= max; value += 1) {
    values.push(value);
  }
  return values;
};

const ComplianceFormCard: React.FC<ComplianceFormCardProps> = ({
  category,
  isExpanded,
  answers,
  onToggle,
  onAnswerChange,
  getQuestionLabel,
  statusMessage,
  statusVariant,
  unsavedAlert,
  disabled = false,
}) => {
  const scaleCache = useMemo(() => {
    const cache = new Map<string, number[]>();
    category.questions.forEach((question) => {
      cache.set(question.id, buildScaleValues(question));
    });
    return cache;
  }, [category.questions]);

  const statusColorMap: Record<
    NonNullable<ComplianceFormCardProps["statusVariant"]>,
    string
  > = {
    default: "text-gray-400",
    saving: "text-amber-600",
    pending: "text-amber-600",
    saved: "text-emerald-600",
    offline: "text-amber-600",
  };
  const statusColor = statusColorMap[statusVariant ?? "default"];

  return (
    <div
      className={`${componentScope("ComplianceForm")} ds-card overflow-hidden transition duration-200 ${
        disabled ? "opacity-70" : "hover:shadow-md"
      }`}
    >
      <Button
        type="button"
        fullWidth
        color="outline"
        onClick={() => {
          if (disabled) return;
          onToggle(category.id);
        }}
        className={`flex min-h-[120px] w-full items-start justify-between gap-4 rounded-none border-0 bg-white px-5 py-4 text-left text-gray-900 shadow-none transition-colors duration-200 hover:bg-gray-50 ${
          disabled ? "cursor-not-allowed opacity-70" : ""
        }`}
        disabled={disabled}
        aria-disabled={disabled}
      >
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-1">
            <p className="text-base font-semibold text-gray-900">{category.name}</p>
            {category.description && (
              <p className="text-sm text-gray-600">{category.description}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-900">
            <span className="text-gray-900">{(category.standardCode || "вЂ”").toUpperCase()}</span>
            {statusMessage && (
              <span
                className={`text-xs font-medium ${
                  disabled ? "text-gray-400" : statusColor
                }`}
              >
                {statusMessage}
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          className={`mt-1 h-5 w-5 shrink-0 text-gray-400 transition-transform ${
            isExpanded ? "rotate-180" : "rotate-0"
          }`}
        />
      </Button>

      <AnimatePresence initial={false}>
        {isExpanded && !disabled && (
          <motion.div
            key={`${category.id}-content`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div className="space-y-4 px-5 py-4">
              {unsavedAlert && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  {unsavedAlert}
                </div>
              )}
              {category.questions.length === 0 ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  No questions configured for this category yet.
                </div>
              ) : (
                category.questions.map((question) => {
                  const scaleValues = scaleCache.get(question.id) ?? [1, 2, 3, 4, 5];
                  const selectedValue = answers[question.id] ?? 0;
                  // ALWAYS prioritize text_en first, regardless of getQuestionLabel result
                  const textEn = typeof question.text_en === "string" ? question.text_en.trim() : "";
                  const resolvedLabel = (getQuestionLabel(category, question) || "").trim();
                  const textDirect = typeof question.text === "string" ? question.text.trim() : "";
                  const textRu = typeof question.text_ru === "string" ? question.text_ru.trim() : "";
                  const textEs = typeof question.text_es === "string" ? question.text_es.trim() : "";
                  
                  // Use text_en if available, otherwise use resolved label, then direct text, then other locales, then code
                  const questionLabel =
                    textEn || 
                    resolvedLabel || 
                    textDirect || 
                    textRu || 
                    textEs || 
                    question.code || 
                    question.id;
                  
                  // Debug: log question label resolution
                  console.log('[ComplianceForm] Question label resolved:', {
                    questionId: question.id,
                    code: question.code,
                    questionLabel,
                    textEn: textEn || 'NOT SET',
                    resolvedLabel: resolvedLabel || 'NOT SET',
                    textDirect: textDirect || 'NOT SET',
                    hasTextEn: !!textEn,
                    hasText: !!textDirect,
                    hasResolvedLabel: !!resolvedLabel,
                  });
                  
                  // Debug: log if we're falling back to code
                  if (!textEn && !resolvedLabel && !textDirect && question.code) {
                    console.warn('[ComplianceForm] Using code as label (no text_en available):', {
                      questionId: question.id,
                      code: question.code,
                      hasTextEn: !!textEn,
                      hasText: !!textDirect,
                      hasResolvedLabel: !!resolvedLabel,
                      questionObject: question,
                    });
                  }
                  const showWeight =
                    question.weight != null && Number(question.weight) !== 1;
                  const showCodeTag =
                    question.code &&
                    questionLabel &&
                    questionLabel.trim().toLowerCase() !== question.code.trim().toLowerCase();

                  return (
                    <div
                      key={question.id}
                      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex flex-col gap-1">
                          <p className="text-sm font-medium text-gray-900">
                            {questionLabel}
                          </p>
                          {showCodeTag && (
                            <span className="text-xs uppercase tracking-wide text-gray-400">
                              {question.code}
                            </span>
                          )}
                        </div>
                        {showWeight && (
                          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                            {`Г—${question.weight}`}
                          </span>
                        )}
                      </div>

                      <div
                        className="flex flex-wrap gap-2"
                        role="radiogroup"
                        aria-label={questionLabel}
                      >
                        {scaleValues.map((value) => {
                          const isSelected = selectedValue === value;
                          return (
                            <label
                              key={`${question.id}-${value}`}
                              className={`inline-flex min-w-[2.5rem] cursor-pointer select-none items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition ${
                                isSelected
                                  ? "bg-[var(--ds-primary,#3b82f6)] text-white shadow"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <input
                                type="radio"
                                name={`question-${category.id}-${question.id}`}
                                value={value}
                                checked={isSelected}
                                onChange={() => onAnswerChange(category.id, question.id, value)}
                                className="sr-only"
                                disabled={disabled}
                              />
                              {value}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ComplianceFormCard;





